import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Film, Volume2, VolumeX, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import CinemaTheater from "../components/CinemaTheater";

/**
 * /screens/:n — single screening room with cinema curtain UX.
 *
 * Flow:
 *  1. Page loads → curtains CLOSED with movie poster + 5-second countdown
 *  2. Countdown finishes → curtains slide open, video starts playing
 *  3. <CinemaTheater> wraps the player with marquee + ambient theater frame
 *  4. User can fullscreen, mute, or hit back to /screens
 */
export default function ScreenPlayer() {
  const { n } = useParams();
  const screenNo = parseInt(n, 10);
  const nav = useNavigate();
  const { user } = useAuth();
  const videoRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paywall, setPaywall] = useState(null);  // { ticket_price, ticket_currency, premiere_label }
  const [ticketBusy, setTicketBusy] = useState(false);

  // Cinema timing
  const [showCountdown, setShowCountdown] = useState(true);
  const [seconds, setSeconds] = useState(5);
  const [curtainsOpen, setCurtainsOpen] = useState(false);
  const [muted, setMuted] = useState(false);

  const loadScreen = async () => {
    setLoading(true);
    setError(null);
    setPaywall(null);
    try {
      const { data } = await api.get(`/screens/${screenNo}`);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 402) {
        const d = e.response.data?.detail;
        if (d && typeof d === "object") setPaywall(d);
        else setError("Active subscription required");
      } else if (e?.response?.status === 404) setError("This screen isn't running today");
      else setError(e?.response?.data?.detail || "Could not load screen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Number.isNaN(screenNo) || screenNo < 1 || screenNo > 20) {
      setError("Invalid screen number");
      setLoading(false);
      return;
    }
    let cancelled = false;
    loadScreen();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenNo]);

  // After Stripe success, poll the ticket status and refresh the screen
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("ticket_session_id");
    if (!sid) return;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/screens/ticket-status/${sid}`);
        if (data.status === "paid") {
          toast.success("Ticket confirmed — taking your seat…");
          window.history.replaceState({}, "", `/screens/${screenNo}`);
          loadScreen();
          return;
        }
      } catch (e) {
        console.warn("[screen] ticket-status poll failed", e?.response?.status);
      }
      if (attempts < 10) setTimeout(tick, 2000);
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenNo]);

  const buyTicket = async () => {
    setTicketBusy(true);
    try {
      const { data } = await api.post("/screens/ticket-checkout", {
        screen_no: screenNo, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ticket checkout failed");
      setTicketBusy(false);
    }
  };

  // Countdown 5 → 4 → 3 → 2 → 1 → curtains open → play
  useEffect(() => {
    if (loading || error || !data) return;
    if (seconds <= 0) {
      setShowCountdown(false);
      setCurtainsOpen(true);
      // Slight delay so the curtain transition has time to start before play
      setTimeout(() => {
        try { videoRef.current?.play(); } catch (e) {
          console.warn("[screen] autoplay blocked", e);
        }
      }, 800);
      return;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, loading, error, data]);

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  const goFullscreen = () => {
    const el = videoRef.current;
    if (!el) return;
    try {
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitEnterFullscreen) el.webkitEnterFullscreen();
    } catch (e) {
      console.warn("[screen] fullscreen failed", e);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-gray-400">Loading screen…</div>;
  }
  if (paywall) {
    const price = paywall.ticket_price?.toFixed(2);
    const hasDiscount = !!paywall.discount_pct && !!paywall.discount_until;
    return (
      <div className="min-h-screen bg-black text-white">
        {/* Sticky top Pay-to-Watch CTA bar — always visible as user scrolls */}
        <div
          data-testid="screen-paywall-sticky-bar"
          className="sticky top-0 z-50 bg-gradient-to-r from-[#E50914] via-[#c40812] to-[#E50914] shadow-2xl border-b border-amber-400/40"
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="hidden sm:flex w-9 h-9 items-center justify-center rounded-full bg-black/30 shrink-0">
                <Film className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-amber-200 font-bold leading-none">
                  Screen {screenNo} · {paywall.premiere_label || "Paid Premiere"}
                </div>
                <div className="text-base sm:text-lg font-black text-white leading-tight truncate">
                  {hasDiscount ? (
                    <CountdownPill
                      until={paywall.discount_until}
                      pct={paywall.discount_pct}
                      label={paywall.discount_label || "EARLY-BIRD"}
                    />
                  ) : "Premiere ticket required"}
                </div>
              </div>
            </div>
            <button
              onClick={buyTicket}
              disabled={ticketBusy}
              data-testid="screen-buy-ticket-sticky"
              className="shrink-0 inline-flex items-center gap-2 bg-white text-[#E50914] hover:bg-amber-50 disabled:opacity-70 px-4 sm:px-6 py-2.5 rounded-md font-black text-sm sm:text-base shadow-lg transition-colors"
            >
              {ticketBusy ? "Loading…" : (
                <>
                  🎟 <span>
                    {hasDiscount && paywall.ticket_price_base && (
                      <span className="line-through text-[#E50914]/60 text-xs mr-1.5">
                        ${paywall.ticket_price_base.toFixed(2)}
                      </span>
                    )}
                    Pay ${price} &amp; Watch
                  </span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Trailer + details below the sticky bar */}
        <div className="flex flex-col items-center justify-center px-6 py-10">
          <div data-testid="screen-premiere-paywall" className="max-w-2xl w-full text-center bg-gradient-to-br from-[#1a0a0a] to-[#0a0a0a] border border-[#E50914]/40 rounded-xl p-8 shadow-2xl">
            <div className="inline-flex items-center gap-1.5 bg-[#E50914] text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded mb-4">
              <Film className="w-3 h-3" /> Premiere
            </div>
            <h1 className="text-3xl font-black mb-2">Screen {screenNo}</h1>
            <p className="text-amber-300 text-sm uppercase tracking-widest mb-4">{paywall.premiere_label || "Paid Premiere"}</p>
            {paywall.trailer_yt && (
              <div className="aspect-video w-full bg-black rounded-lg overflow-hidden border border-white/10 mb-5">
                <iframe
                  src={`https://www.youtube.com/embed/${paywall.trailer_yt}?autoplay=1&mute=1&controls=1&modestbranding=1&rel=0`}
                  title="Trailer"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="screen-paywall-trailer"
                />
              </div>
            )}
            <p className="text-gray-400 leading-relaxed mb-6 max-w-md mx-auto">
              Watched the trailer? Buy your ticket — lights dim, curtains rise, full movie plays in HD.
            </p>
            <div className="text-5xl font-black text-white mb-6 tabular-nums">
              ${price}
              <span className="text-sm text-gray-500 ml-2 uppercase">{paywall.ticket_currency}</span>
            </div>
            <div className="flex flex-col gap-2 max-w-sm mx-auto">
              <button
                onClick={buyTicket}
                disabled={ticketBusy}
                data-testid="screen-buy-ticket"
                className="btn-red px-6 py-3 rounded font-bold disabled:opacity-50"
              >
                {ticketBusy ? "Loading…" : `🎟 Pay $${price} & Watch`}
              </button>
              <Link to="/screens" className="text-gray-400 hover:text-white text-sm mt-1">← Back to lineup</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div data-testid="screen-error" className="max-w-md text-center text-white bg-[#141414] border border-white/10 rounded-lg p-8">
          <Film className="w-10 h-10 text-[#E50914] mx-auto mb-3" />
          <h1 className="text-2xl font-bold mb-2">{error}</h1>
          <Link to="/screens" className="btn-red inline-block px-5 py-2 rounded font-semibold mt-3">← Back to lineup</Link>
        </div>
      </div>
    );
  }

  const { video, screen_label } = data;
  // Resolve playable stream URL (subscription-gated via /api/files in serve_file)
  const streamUrl = video?.stream_url || "";

  return (
    <div className="fixed inset-0 bg-black overflow-hidden" data-testid={`screen-player-${screenNo}`}>
      {/* Back chrome (always visible top-left) */}
      <button
        onClick={() => nav("/screens")}
        data-testid="screen-back"
        className="absolute top-4 left-4 z-50 flex items-center gap-1.5 px-3 py-2 rounded bg-black/60 hover:bg-black/80 backdrop-blur text-white text-sm border border-white/10"
      >
        <ArrowLeft className="w-4 h-4" /> Lobby
      </button>

      {/* Player controls (top-right) */}
      <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
        <button onClick={toggleMute} data-testid="screen-mute" className="p-2 rounded bg-black/60 hover:bg-black/80 backdrop-blur border border-white/10">
          {muted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-white" />}
        </button>
        <button onClick={goFullscreen} data-testid="screen-fullscreen" className="p-2 rounded bg-black/60 hover:bg-black/80 backdrop-blur border border-white/10">
          <Maximize2 className="w-4 h-4 text-white" />
        </button>
      </div>

      {/* Cinema theater wrapper (curtains, marquee, ambient room) */}
      <CinemaTheater
        active={true}
        curtainsOpen={curtainsOpen}
        marqueeTitle={`Now Showing · ${screen_label}`}
        marqueeSub={video?.title}
        intermissionLabel={showCountdown ? `Show begins in ${seconds}` : "Curtains rising…"}
        intermissionContent={
          showCountdown ? (
            <div className="text-center" data-testid="screen-countdown">
              <div className="text-[11px] uppercase tracking-[0.4em] text-amber-300/80 mb-2">{screen_label}</div>
              <div className="text-2xl sm:text-3xl font-black tracking-tight mb-3">{video?.title}</div>
              <div className="text-[12rem] sm:text-[16rem] leading-none font-black text-[#E50914] tabular-nums" style={{ textShadow: "0 0 60px rgba(229,9,20,0.6)" }}>
                {seconds}
              </div>
              <div className="text-xs uppercase tracking-widest text-gray-400 mt-3">Take your seat</div>
            </div>
          ) : null
        }
      >
        {/* The actual video */}
        <video
          ref={videoRef}
          src={streamUrl}
          poster={video?.backdrop || video?.poster}
          className="absolute inset-0 w-full h-full object-contain bg-black"
          controls={curtainsOpen}
          playsInline
          data-testid="screen-video"
          onError={(e) => {
            console.error("[screen] video error", e);
            toast.error("Couldn't load this movie. Try another screen.");
          }}
        />
      </CinemaTheater>
    </div>
  );
}


/* ---------------- CountdownPill: ticking timer for early-bird discounts ---------------- */
function CountdownPill({ until, pct, label }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = Math.max(0, new Date(until).getTime() - now);
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (v) => String(v).padStart(2, "0");
  if (ms <= 0) {
    return <span>Premiere ticket required</span>;
  }
  return (
    <span data-testid="screen-paywall-countdown" className="inline-flex items-center gap-2 flex-wrap">
      <span className="bg-amber-400 text-black text-[10px] sm:text-[11px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest">
        🔥 {label} · {pct}% OFF
      </span>
      <span className="text-white text-sm sm:text-base font-bold tabular-nums">
        ENDS IN {pad(h)}:{pad(m)}:{pad(s)}
      </span>
    </span>
  );
}
